// v1.9 P0: Select 原子组件
// 包装原生 <select>; 统一 label/error/hint/size/block 模式
//
// a11y:
// - label 绑 id
// - error/hint 通过 aria-describedby 关联
import { type SelectHTMLAttributes, forwardRef, useId } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  size?: SelectSize;
  block?: boolean;
  required?: boolean;
  containerClassName?: string;
  options: SelectOption[];
  placeholder?: string;
}

const SIZE_CLASS: Record<SelectSize, string> = {
  sm: 'pl-2.5 pr-7 py-1.5 text-xs',
  md: 'pl-3 pr-9 py-2 text-sm',
  lg: 'pl-3.5 pr-10 py-2.5 text-base',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    error,
    hint,
    size = 'md',
    block = false,
    required,
    containerClassName,
    options,
    placeholder,
    id: idProp,
    className,
    disabled,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const errorId = error ? `${id}-err` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={twMerge(block && 'w-full', containerClassName)}>
      {label && (
        <label htmlFor={id} className="block text-xs text-ink-2 mb-1.5 select-none">
          {label}
          {required && (
            <span className="text-bad ml-0.5" aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={id}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          className={twMerge(
            'w-full bg-surface border rounded-md text-ink appearance-none cursor-pointer',
            'transition-colors duration-150',
            'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15',
            'disabled:bg-surface-2 disabled:text-ink-3 disabled:cursor-not-allowed',
            error
              ? 'border-bad focus:border-bad focus:ring-bad/15'
              : 'border-line hover:border-ink-3',
            SIZE_CLASS[size],
            className,
          )}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <span
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
          aria-hidden
        >
          <Icon name="chevron-down" size={size === 'lg' ? 'md' : 'sm'} />
        </span>
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-xs text-bad mt-1">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-xs text-ink-3 mt-1">
          {hint}
        </p>
      )}
    </div>
  );
});
